import React, { useState, useEffect, useRef } from 'react';
import {
    MapPin, UserPlus, Users, TrendingUp, Search, Info, RefreshCw,
    Eye, DollarSign, Globe,
} from 'lucide-react';
import toast from 'react-hot-toast';

const StatCard = ({ Icon, label, value, sub, accent }) => (
    <div className="bg-gray-50 rounded-lg border border-gray-100 p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-1.5">
            <span
                className="flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0"
                style={{ background: `${accent}1a`, color: accent }}
            >
                <Icon className="w-3.5 h-3.5" />
            </span>
            <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</span>
        </div>
        <div className="text-xl sm:text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
        {sub != null && <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">{sub}</div>}
    </div>
);

const formatCurrency = (val) => {
    if (val === null || val === undefined || Number.isNaN(Number(val))) return '—';
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 0,
    }).format(Number(val));
};

const ChannelSeoPanel = ({
    title,
    fetchData,
    errorLabel,
    helpText,
    utmExample,
    accent = '#0e9f6e',
    startDate,
    endDate,
    embedded = false,
}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [helpOpen, setHelpOpen] = useState(false);
    const loadTokenRef = useRef(0);

    useEffect(() => {
        const myToken = ++loadTokenRef.current;
        setLoading(true);
        fetchData(startDate, endDate)
            .then((res) => { if (myToken === loadTokenRef.current) setData(res); })
            .catch((err) => {
                if (myToken !== loadTokenRef.current) return;
                console.error(`${errorLabel} load failed:`, err);
                toast.error(`Failed to load ${errorLabel}`);
            })
            .finally(() => { if (myToken === loadTokenRef.current) setLoading(false); });
    }, [startDate, endDate, fetchData, errorLabel]);

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-16 text-gray-500 text-sm">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                Loading {title}…
            </div>
        );
    }

    const totals = data?.totals || {};
    const byPage = data?.by_page || [];
    const byKeyword = data?.by_keyword || [];
    const maxPageSignups = Math.max(1, ...byPage.map((p) => p.signups));
    const noData = (totals.visitors || 0) === 0 && (totals.signups || 0) === 0;

    const content = (
        <div className="space-y-5">
            {!embedded && (
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Globe className="w-4 h-4" style={{ color: accent }} />
                    {title}
                </h3>
            )}

            <button
                type="button"
                onClick={() => setHelpOpen((o) => !o)}
                className="w-full text-left rounded-lg px-3 py-2.5 flex gap-2 items-start border transition-colors hover:bg-gray-50"
                style={{ background: `${accent}08`, borderColor: `${accent}25` }}
            >
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: accent }} />
                <div className="min-w-0 flex-1">
                    <span className="text-xs font-medium text-gray-700">
                        How {title} is tracked
                        <span className="text-gray-400 font-normal ml-1">{helpOpen ? '▲' : '▼'}</span>
                    </span>
                    {helpOpen && (
                        <p className="text-xs text-gray-600 leading-relaxed mt-1.5">
                            {helpText}
                            {utmExample && (
                                <>
                                    {' '}Use{' '}
                                    <code className="px-1 py-0.5 bg-white rounded border border-gray-200 text-[10px]">
                                        {utmExample}
                                    </code>
                                    {' '}on portal links.
                                </>
                            )}
                        </p>
                    )}
                </div>
            </button>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <StatCard Icon={Users} label="Visitors" value={totals.visitors ?? 0}
                          sub="Unique sessions" accent="#2563eb" />
                <StatCard Icon={UserPlus} label="Signups" value={totals.signups ?? 0}
                          sub="Accounts created" accent={accent} />
                <StatCard Icon={MapPin} label="Enquiries" value={totals.enquiries ?? 0}
                          sub="Leads submitted" accent="#7c3aed" />
                <StatCard Icon={Eye} label="Property views" value={totals.properties_viewed ?? 0}
                          sub={`${totals.distinct_properties_viewed ?? 0} unique listings`} accent="#0891b2" />
                <StatCard Icon={TrendingUp} label="Conv. rate" value={`${totals.conversion_rate ?? 0}%`}
                          sub="Signups ÷ visitors" accent="#d97706" />
                <StatCard Icon={DollarSign} label="Revenue" value={formatCurrency(totals.revenue_commission)}
                          sub={`${totals.attributed_sales ?? 0} attributed sale${(totals.attributed_sales ?? 0) === 1 ? '' : 's'}`}
                          accent="#059669" />
            </div>

            {noData ? (
                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/80 p-10 text-center">
                    <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-700">No {title} traffic in this range yet</p>
                    <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
                        Once tagged visitors arrive and convert, metrics will appear here.
                    </p>
                </div>
            ) : (
                <>
                    <div className="rounded-lg border border-gray-200 overflow-hidden">
                        <h4 className="text-sm font-semibold text-gray-900 px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                            <MapPin className="w-4 h-4" style={{ color: accent }} />
                            By page / campaign
                        </h4>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs text-gray-400 bg-white border-b border-gray-100">
                                        <th className="px-4 py-2.5 font-medium">Page</th>
                                        <th className="px-4 py-2.5 font-medium min-w-[140px]">Sign-ups</th>
                                        <th className="px-4 py-2.5 font-medium text-right">Visitors</th>
                                        <th className="px-4 py-2.5 font-medium text-right">Enquiries</th>
                                        <th className="px-4 py-2.5 font-medium text-right">Views</th>
                                        <th className="px-4 py-2.5 font-medium text-right">Conv.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {byPage.map((p) => (
                                        <tr key={p.page} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                                            <td className="px-4 py-2.5 text-gray-700 capitalize">{p.page}</td>
                                            <td className="px-4 py-2.5">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden max-w-[120px]">
                                                        <div
                                                            className="h-3 rounded-full"
                                                            style={{
                                                                width: `${Math.max(8, (p.signups / maxPageSignups) * 100)}%`,
                                                                background: accent,
                                                            }}
                                                        />
                                                    </div>
                                                    <span className="text-xs font-semibold text-gray-900 tabular-nums">{p.signups}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums">{p.visitors}</td>
                                            <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums">{p.enquiries}</td>
                                            <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums">{p.properties_viewed ?? 0}</td>
                                            <td className="px-4 py-2.5 text-right text-gray-600 tabular-nums">{p.conversion_rate}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {byKeyword.length > 0 && (
                        <div className="rounded-lg border border-gray-200 p-4">
                            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <Search className="w-4 h-4" style={{ color: accent }} />
                                Keywords that converted
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {byKeyword.map((k) => (
                                    <span
                                        key={k.keyword}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                                        style={{ background: `${accent}14`, color: accent }}
                                    >
                                        {k.keyword}
                                        <span
                                            className="px-1.5 py-0.5 text-white rounded-full text-[10px] font-bold"
                                            style={{ background: accent }}
                                        >
                                            {k.signups}
                                        </span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    if (embedded) {
        return content;
    }

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
            {content}
        </div>
    );
};

export default ChannelSeoPanel;
